'use client';

import { c as _c } from "react/compiler-runtime";
import type { ElementType, ReactNode } from 'react';
import SvgIcon from '@mui/material/SvgIcon';
import InfoIcon from '@mui/icons-material/Info';
import ErrorIcon from '@mui/icons-material/Error';
import WarningIcon from '@mui/icons-material/Warning';
import HelpIcon from '@mui/icons-material/Help';
import CheckIcon from '@mui/icons-material/CheckCircle';
import { styled, SxProps, Theme } from '../styles';
import Typography from '../Typography';
import { getTypeColors, getDynamicIconStyles } from './utils';
export type Type = 'info' | 'warning' | 'error' | 'question' | 'success';
export interface Props {
  className?: string;
  children: ReactNode;
  type?: Type;
  isSmall?: boolean;
  id?: string;
  shouldUseDangerousHtml?: boolean;
  isIllustrationHidden?: boolean;
  isIllustrationStickedOnTop?: boolean;
  illustrationElement?: ReactNode;
  sx?: SxProps<Theme>;
  icon?: ElementType;
  role?: 'alert' | 'status' | 'log' | 'marquee' | 'timer';
}
const Container = styled('div', {
  shouldForwardProp: prop => !['isSmall', 'isCentered'].includes(prop as string)
})<{
  isSmall?: boolean;
  isCentered: boolean;
}>(({
  theme: {
    spacing
  },
  isSmall,
  isCentered
}) => ({
  display: 'flex',
  flexDirection: 'row',
  gap: isSmall ? spacing(1) : spacing(2),
  padding: isSmall ? spacing(1) : spacing(2),
  alignItems: isCentered ? 'center' : undefined
}));
const ContentWrapper = styled('div')(({
  theme: {
    palette
  }
}) => ({
  minWidth: 0,
  color: palette.text.onMain,
  '& a': {
    color: 'inherit'
  }
}));
const Wrapper = styled('div')<{
  type: Type;
}>(({
  type
}) => ({
  lineHeight: '1rem',
  ...getTypeColors(type)
}));
const iconByType: Record<Type, ElementType> = {
  info: InfoIcon,
  warning: WarningIcon,
  error: ErrorIcon,
  question: HelpIcon,
  success: CheckIcon
};
const Callout = t0 => {
  const $ = _c(24);
  const {
    children,
    type: t1,
    className: t2,
    isSmall,
    id,
    illustrationElement: t3,
    isIllustrationHidden,
    isIllustrationStickedOnTop,
    shouldUseDangerousHtml,
    sx: t4,
    icon,
    role
  } = t0;
  const type = t1 === undefined ? "info" : t1;
  const className = t2 === undefined ? "" : t2;
  const illustrationElement = t3 === undefined ? undefined : t3;
  let t5;
  if ($[0] !== t4) {
    t5 = t4 === undefined ? {} : t4;
    $[0] = t4;
    $[1] = t5;
  } else {
    t5 = $[1];
  }
  const sx = t5;
  const Icon = icon ?? iconByType[type];
  const variantTypography = isSmall ? "bodySmall" : "body";
  const isCentered = !isIllustrationStickedOnTop && Boolean(illustrationElement);
  let t6;
  if ($[2] !== Icon || $[3] !== illustrationElement || $[4] !== isIllustrationHidden || $[5] !== isSmall || $[6] !== type) {
    t6 = !isIllustrationHidden && <div data-testid="callout-illustration">{!illustrationElement ? <SvgIcon component={Icon} sx={{
        "&&": getDynamicIconStyles(Boolean(isSmall), type)
      }} /> : illustrationElement}</div>;
    $[2] = Icon;
    $[3] = illustrationElement;
    $[4] = isIllustrationHidden;
    $[5] = isSmall;
    $[6] = type;
    $[7] = t6;
  } else {
    t6 = $[7];
  }
  let t7;
  if ($[8] !== children || $[9] !== shouldUseDangerousHtml || $[10] !== variantTypography) {
    t7 = <ContentWrapper>{shouldUseDangerousHtml ? <Typography variant={variantTypography} component="div" dangerouslySetInnerHTML={{
        __html: children as string
      }} /> : <Typography variant={variantTypography} component="div">{children}</Typography>}</ContentWrapper>;
    $[8] = children;
    $[9] = shouldUseDangerousHtml;
    $[10] = variantTypography;
    $[11] = t7;
  } else {
    t7 = $[11];
  }
  let t8;
  if ($[12] !== isCentered || $[13] !== isSmall || $[14] !== t6 || $[15] !== t7) {
    t8 = <Container isSmall={isSmall} isCentered={isCentered} data-testid="callout-container">{t6}{t7}</Container>;
    $[12] = isCentered;
    $[13] = isSmall;
    $[14] = t6;
    $[15] = t7;
    $[16] = t8;
  } else {
    t8 = $[16];
  }
  let t9;
  if ($[17] !== className || $[18] !== id || $[19] !== role || $[20] !== sx || $[21] !== t8 || $[22] !== type) {
    t9 = <Wrapper sx={sx} className={className} type={type} data-testid="callout" id={id} role={role}>{t8}</Wrapper>;
    $[17] = className;
    $[18] = id;
    $[19] = role;
    $[20] = sx;
    $[21] = t8;
    $[22] = type;
    $[23] = t9;
  } else {
    t9 = $[23];
  }
  return t9;
};
export default Callout;